import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Select, DatePicker, Row, Col, Space, message } from "antd";
import { PlusOutlined, DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import FacturasTable from "./FacturasTable";
import PayAllInvoicesButton from "../../../../components/PayAllInvoicesButton/PayAllInvoicesButton";
import { getFacturas, getClients } from "../store/thunks";
import facturaApi from "../store/api";
import { shortId } from "helper/util";
import {
  readFacturaListState,
  writeFacturaListState,
  getFacturaIdSortValue
} from "../helper";
import {
  selectAllFacturas,
  selectMetadata,
  selectLoading,
  selectClients
} from "../store/slice";
import "./FacturasList.scss";

const LIST_STATE_KEY = "admin-facturas";

const { Option } = Select;
const { RangePicker } = DatePicker;

const FacturasList = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [flashingFacturaId, setFlashingFacturaId] = useState(null);

  const facturasData = useSelector(selectAllFacturas);
  const metadata = useSelector(selectMetadata);
  const loading = useSelector(selectLoading);
  const clients = useSelector(selectClients);

  // Default date range: previous month + current month
  const getDefaultDateRange = () => {
    const now = moment();
    const startOfLastMonth = now.clone().subtract(1, "month").startOf("month");
    const endOfCurrentMonth = now.clone().endOf("month");
    return [startOfLastMonth, endOfCurrentMonth];
  };

  // Start from whatever the user last had on this list (so returning from an invoice
  // keeps their search), with URL parameters winning when there are any.
  const getInitialState = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const clientIdParam = searchParams.get("clientId");
    const statusParam = searchParams.get("status");

    const restored = readFacturaListState(LIST_STATE_KEY, {
      filters: {
        clientId: null,
        dateRange: getDefaultDateRange(),
        status: [],
        facturaType: []
      },
      currentPage: 1,
      sortConfig: { key: "facturaId", direction: "descending" }
    });

    if (!clientIdParam && !statusParam) return restored;

    return {
      ...restored,
      currentPage: 1,
      filters: {
        ...restored.filters,
        clientId: clientIdParam || restored.filters.clientId,
        status: statusParam ? statusParam.split(",") : restored.filters.status
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const [filters, setFilters] = useState(getInitialState.filters);
  const [sortConfig, setSortConfig] = useState(getInitialState.sortConfig);
  // Frontend pagination - handle page changes without backend calls
  const [currentPage, setCurrentPage] = useState(getInitialState.currentPage);
  const pageSize = 10;

  const loadFacturas = React.useCallback(() => {
    const params = {
      page: 1,
      pageSize: 1000 // Load all data to enable proper sorting across all records
    };

    // Add date range filter
    if (filters.dateRange && filters.dateRange.length === 2) {
      params.startDate = filters.dateRange[0].format("YYYY-MM-DD");
      params.endDate = filters.dateRange[1].format("YYYY-MM-DD");
    }

    // Add client filter
    if (filters.clientId) {
      params.clientId = filters.clientId;
    }

    // Add status filter
    if (filters.status && filters.status.length > 0) {
      params.status = filters.status.join(",");
    }

    // Add facturaType filter
    if (filters.facturaType && filters.facturaType.length > 0) {
      params.facturaType = filters.facturaType.join(",");
    }

    dispatch(getFacturas(params));
  }, [
    filters.dateRange,
    filters.clientId,
    filters.status,
    filters.facturaType,
    dispatch
  ]);

  useEffect(() => {
    // Load clients for filter dropdown
    dispatch(getClients());

    // Load initial facturas data with default filters
    loadFacturas();
  }, [dispatch, loadFacturas]);

  // Reload facturas when filters change
  useEffect(() => {
    loadFacturas();
  }, [loadFacturas]);

  useEffect(() => {
    // Check if we're returning from create with a newly created factura
    const searchParams = new URLSearchParams(location.search);
    const newFacturaId = searchParams.get("created");

    if (newFacturaId) {
      // Set flashing animation
      setFlashingFacturaId(newFacturaId);

      // Remove the flash after 3 seconds
      setTimeout(() => {
        setFlashingFacturaId(null);
      }, 3000);

      // Clean up URL
      history.replace("/admin/facturas");
    }
  }, [location.search, history]);

  // Update filters when URL changes (e.g., navigating from Receivables report)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const clientIdParam = searchParams.get("clientId");
    const statusParam = searchParams.get("status");

    // Only update if URL has filter parameters
    if (clientIdParam || statusParam) {
      setFilters((prev) => ({
        ...prev,
        clientId: clientIdParam || prev.clientId,
        status: statusParam ? statusParam.split(",") : prev.status
      }));
    }
  }, [location.search]);

  // Keep the list state around so opening an invoice and coming back doesn't wipe it
  useEffect(() => {
    writeFacturaListState(LIST_STATE_KEY, { filters, currentPage, sortConfig });
  }, [filters, currentPage, sortConfig]);

  // Only offer clients that actually have invoices inside the selected date range,
  // so it's obvious at a glance who has been billed for that period.
  const [clientsInRange, setClientsInRange] = useState(null);

  const dateRangeKey =
    filters.dateRange && filters.dateRange.length === 2
      ? `${filters.dateRange[0].format("YYYY-MM-DD")},${filters.dateRange[1].format(
          "YYYY-MM-DD"
        )}`
      : "";

  useEffect(() => {
    if (!dateRangeKey) {
      setClientsInRange(null);
      return undefined;
    }

    let cancelled = false;
    const [startDate, endDate] = dateRangeKey.split(",");

    facturaApi
      .getClientsWithInvoices({ startDate, endDate })
      .then((data) => {
        if (!cancelled) setClientsInRange(Array.isArray(data) ? data : []);
      })
      .catch((error) => {
        console.error("Failed to load clients for the selected date range:", error);
        // Fall back to the full client list rather than leaving the filter empty
        if (!cancelled) setClientsInRange(null);
      });

    return () => {
      cancelled = true;
    };
  }, [dateRangeKey]);

  const clientOptions = React.useMemo(() => {
    const allClients = Array.isArray(clients) ? clients : [];
    if (!clientsInRange) return allClients;

    const options = clientsInRange.filter((client) => client?.id);

    // Keep a client that is already selected visible even if the range excludes it
    if (filters.clientId && !options.some((client) => client.id === filters.clientId)) {
      const selected = allClients.find((client) => client.id === filters.clientId);
      if (selected) {
        return [...options, { id: selected.id, clientName: selected.clientName }];
      }
    }

    return options;
  }, [clients, clientsInRange, filters.clientId]);

  const handleCreateFactura = () => {
    history.push("/admin/facturas/create");
  };

  const handleEditFactura = (facturaId) => {
    history.push(`/admin/facturas/edit/${facturaId}`);
  };

  const handleRefresh = () => {
    loadFacturas();
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change

    // Force immediate reload instead of relying on useEffect
    setTimeout(() => {
      const newFilters = {
        ...filters,
        [filterType]: value
      };

      const params = {
        page: 1,
        pageSize: 1000
      };

      // Add date range filter
      if (newFilters.dateRange && newFilters.dateRange.length === 2) {
        params.startDate = newFilters.dateRange[0].format("YYYY-MM-DD");
        params.endDate = newFilters.dateRange[1].format("YYYY-MM-DD");
      }

      // Add client filter
      if (newFilters.clientId) {
        params.clientId = newFilters.clientId;
      }

      // Add status filter
      if (newFilters.status && newFilters.status.length > 0) {
        params.status = newFilters.status.join(",");
      }

      // Add facturaType filter
      if (newFilters.facturaType && newFilters.facturaType.length > 0) {
        params.facturaType = newFilters.facturaType.join(",");
      }

      dispatch(getFacturas(params));
    }, 0);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Get paginated data from sorted data
  const getPaginatedData = () => {
    const sortedData = getSortedData();
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  };

  // Sorting functions
  const getSortedData = () => {
    if (!sortConfig.key) return facturasData;

    const sortedData = [...facturasData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle date values
      if (sortConfig.key === "facturaDate") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle numeric values
      if (sortConfig.key === "total" || sortConfig.key === "totalPaid") {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      // Handle status (custom order: unpaid, partial, paid, overdue, cancelled)
      if (sortConfig.key === "status") {
        const statusOrder = { unpaid: 0, partial: 1, paid: 2, overdue: 3, cancelled: 4 };
        aVal = statusOrder[aVal] || 0;
        bVal = statusOrder[bVal] || 0;
      }

      // Handle facturaType (alphabetical order)
      if (sortConfig.key === "facturaType") {
        aVal = aVal || "Manual";
        bVal = bVal || "Manual";
      }

      // Handle facturaId (sort by date first, then by duplicate suffix)
      if (sortConfig.key === "facturaId") {
        aVal = getFacturaIdSortValue(aVal);
        bVal = getFacturaIdSortValue(bVal);
      }

      if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });

    return sortedData;
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handleExportFacturaPdf = async (facturaId) => {
    try {
      const response = await facturaApi.exportFacturaPdf(facturaId);
      const blob = new Blob([response], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.target = "_blank";
      const factura = facturasData.find((f) => f.id === facturaId);
      const facturaIdShort = factura?.facturaId || shortId(facturaId);
      link.download = `factura-${facturaIdShort}-${new Date().getTime()}.pdf`;
      window.open(link.href, "_blank");
    } catch (err) {
      console.log(err);
      message.error("Failed to export PDF");
    }
  };

  const exportToCSV = () => {
    try {
      const dataToExport = getSortedData();

      if (dataToExport.length === 0) {
        message.warning("No data to export");
        return;
      }

      // Define CSV headers
      const headers = ["Date", "Factura ID", "Status", "Total", "Paid", "Type", "Client"];

      // Convert data to CSV format
      const csvContent = [
        headers.join(","),
        ...dataToExport.map((row) =>
          [
            new Date(row.facturaDate).toISOString().split("T")[0],
            `"${row.facturaId}"`,
            row.status,
            row.total || 0,
            row.totalPaid || 0,
            row.facturaType || "Manual",
            `"${row.client?.clientName || ""}"`
          ].join(",")
        )
      ].join("\n");

      // Create and download the file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `facturas-export-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("CSV exported successfully");
    } catch (error) {
      console.error("Export failed:", error);
      message.error("Failed to export CSV");
    }
  };

  return (
    <div className="facturas-list">
      <div className="facturas-header">
        <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={24}>
            <Card size="small">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={5}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Client</div>
                  <Select
                    placeholder="All clients"
                    value={filters.clientId}
                    onChange={(value) => handleFilterChange("clientId", value)}
                    allowClear
                    style={{ width: "100%" }}
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                  >
                    {clientOptions.map((client) => (
                      <Option key={client.id} value={client.id}>
                        {client.clientName}
                      </Option>
                    ))}
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={7}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Date Range</div>
                  <RangePicker
                    value={filters.dateRange}
                    onChange={(dates) => handleFilterChange("dateRange", dates)}
                    format="YYYY-MM-DD"
                    style={{ width: "100%" }}
                  />
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Status</div>
                  <Select
                    mode="multiple"
                    placeholder="All statuses"
                    value={filters.status}
                    onChange={(value) => handleFilterChange("status", value)}
                    allowClear
                    style={{ width: "100%" }}
                  >
                    <Option value="unpaid">Unpaid</Option>
                    <Option value="partial">Partial</Option>
                    <Option value="paid">Paid</Option>
                    <Option value="overdue">Overdue</Option>
                    <Option value="cancelled">Cancelled</Option>
                  </Select>
                </Col>

                <Col xs={24} sm={12} md={6}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Type</div>
                  <Select
                    mode="multiple"
                    placeholder="All types"
                    value={filters.facturaType}
                    onChange={(value) => handleFilterChange("facturaType", value)}
                    allowClear
                    style={{ width: "100%" }}
                  >
                    <Option value="Managed">Managed</Option>
                    <Option value="SaaS">SaaS</Option>
                    <Option value="Manual">Manual</Option>
                  </Select>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col
            xs={24}
            lg={6}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start"
            }}
          >
            <Space>
              <PayAllInvoicesButton
                facturas={getSortedData()}
                onSuccess={handleRefresh}
                size="small"
                isClientSide={false}
                disabled={!filters.clientId}
                clientName={
                  filters.clientId
                    ? clients.find((c) => c.id === filters.clientId)?.clientName
                    : ""
                }
              />
              <Button size="small" icon={<DownloadOutlined />} onClick={exportToCSV}>
                Export CSV
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleCreateFactura}
              >
                Create Factura
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="content-table">
        <FacturasTable
          dataSource={getPaginatedData()}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: facturasData.length,
            size: "small",
            showSizeChanger: false,
            onChange: handlePageChange
          }}
          onRowClick={handleEditFactura}
          flashingFacturaId={flashingFacturaId}
          loading={loading}
          onRefresh={handleRefresh}
          sortConfig={sortConfig}
          onSort={handleSort}
          onViewPDF={handleExportFacturaPdf}
        />
      </div>
    </div>
  );
};

export default FacturasList;
